import React, { useEffect, useRef, useState } from 'react'

// The app's Year / Make / Model / Trim cascade, shared by the modals on this
// page. Callers pass their own `isUsed` and year range: a car being sold today
// and a car the customer already owns come from different catalogs.

// The cars API answers with {cleaned_name} or {name} depending on the level.
export const toOptionStrings = (rawList) =>
    (rawList || []).map((item) => item?.cleaned_name || item?.name).filter(Boolean).sort()

// A value that came off a quote (or a VIN decode) may not appear in the fetched
// list (or the list may not have loaded yet) — keep it selectable either way.
export const withCurrent = (options, value) =>
    (value && !options.includes(value)) ? [value, ...options] : options

// One level of the vehicle cascade: refetch its options whenever its inputs
// change, drop out-of-order responses, and empty out while a parent level is
// unset. `deps` is fixed per call site, so the hook order stays stable.
export const useOptions = (enabled, fetchOptions, deps) => {
    const [options, setOptions] = useState([])
    const seq = useRef(0)
    useEffect(() => {
        if (!enabled) { setOptions([]); return }
        const mySeq = ++seq.current
        fetchOptions()
            .then((res) => { if (mySeq === seq.current) setOptions(toOptionStrings(res?.data)) })
            .catch(() => {})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps)
    return options
}

// Plain selects so they inherit the calling modal's field styling and survive
// the Dialog portal (MUI menu Papers render outside it). `showLabel` renders the
// level's name above the field; without it the name lives only in the select's
// placeholder, which is how the sales flow has always shown it.
export const VehicleSelect = ({ label, value, options, disabled, onChange, showLabel }) => (
    <div className="mfield">
        {showLabel && <div className="mfield-label">{label}</div>}
        <select
            aria-label={label}
            value={value || ''}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
        >
            {/* Names the field while empty, but `hidden` keeps it out of the
                open list so "Year"/"Make"/… are never offered as choices. */}
            <option value="" disabled hidden>{label}</option>
            {withCurrent(options, value).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
    </div>
)

// A cascade level that may already know its options. A VIN decode returns the
// make/model/trim lists for the car it resolved, so when a level is handed one
// it is used as-is and no request is made; otherwise the level fetches. Keeping
// both halves of that rule here stops a caller from stating it twice and
// letting the two drift.
export const useCascadeOptions = (preset, enabled, fetchOptions, deps) => {
    const fetched = useOptions(enabled && !preset, fetchOptions, deps)
    return preset || fetched
}

// Every model year a customer might still be driving, newest first. The sales
// flow keeps its own three-year list — a car being sold now is new inventory.
// Lower bound matches Calculate's used-car range.
export const allYearOptions = () => {
    const nextYear = new Date().getFullYear() + 1
    return Array.from({ length: nextYear - 2015 + 1 }, (_, i) => String(2015 + i)).reverse()
}
